import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import type { InterviewKind } from '@Domain/Enums/Interviews/InterviewKind';
import type PlainDate from '@Domain/PlainDate';
import type {
	LocalInterviewParticipant,
	LocalInterviewSource,
} from '@Domain/Ports/LocalInterviews/LocalInterviewPort';

/**
 * Body of the composite create call: the interview, its consent and its recording are created
 * together, in one request. `facilityId`/`subject` are route identifiers and never enter the
 * body — omitting them is `toJSON()`'s one piece of logic, and the one thing tests exercise.
 * `consentGrantedAt` is the epoch-ms instant read from `LocalInterview.consentGrantedAt` — `null`
 * on the import path only. `kind` is never typed by the user: it is decided by which capture button
 * was clicked, carried unchanged from `newInterviewDraftSignal` through the local interview to here —
 * the server requires it, with no default.
 * `participantsInformedAt` is the epoch-ms instant the participants were informed, sent in place of the
 * consent for a dossier type that asks for that notice; `null` otherwise.
 * `preparationId` is the preparation the interview is attached to on creation, `null` when it was
 * captured outside any preparation.
 *
 * `participants` takes the single list the caller actually holds
 * (`LocalInterview.participants: readonly LocalInterviewParticipant[]`) rather than two separate
 * `readonly number[]` parameters: splitting it by `kind` into `participantContactIds` and
 * `participantStaffMemberIds` is logic, and `toJSON()` already carries this Command's logic (omitting
 * the route identifiers, omitting `consent`) — so it belongs there too, not at every call site.
 */
class CreateInterview {
	constructor(
		public readonly facilityId: number,
		public readonly subject: SubjectAddress,
		public readonly clientReference: string,
		public readonly name: string,
		public readonly kind: InterviewKind,
		public readonly conductedOn: PlainDate,
		public readonly participants: readonly LocalInterviewParticipant[],
		public readonly consentGrantedAt: number | null,
		public readonly source: LocalInterviewSource,
		public readonly preparationId: number | null,
		public readonly participantsInformedAt: number | null = null,
	) {
		Object.freeze(this);
	}

	/**
	 * `consent` is omitted, never sent as `null`: the server rejects `consent: null` on a captured
	 * path, and always emitting the key would produce exactly that whenever no consent has been
	 * granted yet (the import path). `participantContactIds`/`participantStaffMemberIds` follow the
	 * same logic: omitted, never sent as an empty array, when there is no participant of that kind —
	 * the server treats both as optional.
	 */
	toJSON(): Record<string, unknown> {
		const body: Record<string, unknown> = {
			clientReference: this.clientReference,
			name: this.name,
			kind: this.kind,
			conductedOn: this.conductedOn.toApi(),
			recording: { source: this.source },
		};

		const participantContactIds = this.participants
			.filter((participant) => participant.kind === 'contact')
			.map((participant) => participant.id);
		const participantStaffMemberIds = this.participants
			.filter((participant) => participant.kind === 'staff')
			.map((participant) => participant.id);

		if (participantContactIds.length > 0) {
			body.participantContactIds = participantContactIds;
		}

		if (participantStaffMemberIds.length > 0) {
			body.participantStaffMemberIds = participantStaffMemberIds;
		}

		if (this.consentGrantedAt !== null) {
			body.consent = { grantedAt: new Date(this.consentGrantedAt).toISOString() };
		}

		if (this.participantsInformedAt !== null) {
			body.participantsInformedAt = new Date(this.participantsInformedAt).toISOString();
		}

		if (this.preparationId !== null) {
			body.preparation = this.preparationId;
		}

		return body;
	}
}

export default CreateInterview;
